import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import type { FirmIntegrationMeta } from '../types';

type SiteToken = { siteFormToken: string | null; slug: string | null };
type IntegrationsList = { items: FirmIntegrationMeta[] };

/**
 * Lead-intake settings: Meta Lead Ads credentials (App ID, App Secret,
 * Page Access Token, verify token) + the public site-form token used by
 * the embed.js script. Until E12 lands the Meta values are entered by hand;
 * after E12 the browser-automation flow writes them straight to /api/firm-integrations.
 */
export function LeadsSettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const [meta, setMeta] = useState<FirmIntegrationMeta | null>(null);
  const [site, setSite] = useState<SiteToken | null>(null);
  const [appId, setAppId] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [pageToken, setPageToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      const [list, tok] = await Promise.all([
        apiFetch<IntegrationsList>('/api/firm-integrations'),
        apiFetch<SiteToken>('/api/firm-integrations/site-token'),
      ]);
      const m = list.items.find((i) => i.kind === 'meta') ?? null;
      setMeta(m);
      setSite(tok);
      setAppId(m?.externalId ?? '');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function saveMeta() {
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      await apiFetch('/api/firm-integrations/meta', {
        method: 'PUT',
        body: JSON.stringify({
          externalId: appId.trim(),
          // Empty string = "leave existing" on the server
          appSecret: appSecret.trim(),
          pageAccessToken: pageToken.trim(),
        }),
      });
      setAppSecret('');
      setPageToken('');
      setMsg(t('messages.saved'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function removeMeta() {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/api/firm-integrations/meta', { method: 'DELETE' });
      setMsg(t('messages.removed'));
      setAppId('');
      setAppSecret('');
      setPageToken('');
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function rotateSiteToken() {
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/api/firm-integrations/site-token/rotate', { method: 'POST' });
      setMsg(t('messages.token_rotated'));
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const webhookUrl = `${window.location.origin}/webhooks/meta`;
  const embedSrc =
    site?.slug && site.siteFormToken
      ? `${window.location.origin}/embed.js?firm=${encodeURIComponent(site.slug)}&token=${site.siteFormToken}`
      : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="form-card modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="form-card-header">
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{t('settings.title')}</div>
          </div>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onClose}
            aria-label={t('common:actions.close')}
          >
            ×
          </button>
        </div>

        <div className="form-section">
          <div className="section-label">{t('settings.meta_section')}</div>
          <div className="field-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="field">
              <label>{t('settings.meta_app_id')}</label>
              <input value={appId} onChange={(e) => setAppId(e.target.value)} />
            </div>
            <div className="field">
              <label>
                {t('settings.meta_app_secret')}
                {meta?.hasAppSecret && ' ✓'}
              </label>
              <input
                type="password"
                value={appSecret}
                onChange={(e) => setAppSecret(e.target.value)}
                placeholder={meta?.hasAppSecret ? '••••••••' : ''}
              />
            </div>
            <div className="field">
              <label>
                {t('settings.meta_page_token')}
                {meta?.hasPageAccessToken && ' ✓'}
              </label>
              <input
                type="password"
                value={pageToken}
                onChange={(e) => setPageToken(e.target.value)}
                placeholder={meta?.hasPageAccessToken ? '••••••••' : ''}
              />
            </div>
            {meta?.metaVerifyToken && (
              <>
                <div className="field">
                  <label>{t('settings.meta_verify_token')}</label>
                  <input value={meta.metaVerifyToken} readOnly />
                </div>
                <div className="field">
                  <label>{t('settings.meta_webhook_url')}</label>
                  <input value={webhookUrl} readOnly />
                </div>
              </>
            )}
          </div>
          <div className="form-actions" style={{ marginTop: 10 }}>
            {meta && (
              <button className="btn-cancel" onClick={removeMeta} disabled={busy}>
                {t('actions.remove_meta')}
              </button>
            )}
            <button className="btn-primary" onClick={saveMeta} disabled={busy}>
              {t('actions.save_meta')}
            </button>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('settings.site_section')}</div>
          {site?.siteFormToken ? (
            <>
              <div className="field">
                <label>{t('settings.site_token')}</label>
                <input value={site.siteFormToken} readOnly />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)', margin: '8px 0' }}>
                {t('settings.site_token_hint')}
              </div>
              {embedSrc && (
                <pre
                  style={{
                    background: 'var(--surface2)',
                    padding: 10,
                    borderRadius: 6,
                    overflow: 'auto',
                    fontSize: 12,
                  }}
                >
                  {`<script src="${embedSrc}" defer></script>\n<form data-lexcrm-lead>\n  <input name="name" />\n  <input name="phone" />\n  <input name="email" />\n  <button>Send</button>\n</form>`}
                </pre>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>
              {t('settings.site_token_missing')}
            </div>
          )}
          <div className="form-actions" style={{ marginTop: 10 }}>
            <button className="btn-cancel" onClick={rotateSiteToken} disabled={busy}>
              {t('actions.rotate_token')}
            </button>
          </div>
        </div>

        {msg && (
          <div className="form-section" style={{ color: 'var(--green)' }}>
            {msg}
          </div>
        )}
        {err && (
          <div className="form-section" style={{ color: 'var(--red)' }}>
            {err}
          </div>
        )}
      </div>
    </div>
  );
}
